import {
  Field,
  FileField,
  Image,
  ImageField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { sanitize } from 'isomorphic-dompurify';

import { icon_download } from '@gn-audio/icons';
import { Button, ComponentProps, JabraIcon } from '@gn-audio/jss-components';

import { Eyebrow } from 'common/components/Eyebrow';
import { SustainabilitySpotBlock } from 'composables/Product/SustainabilitySpot/SustainabilitySpotBlock';

export type SustainabilitySpotFields = {
  blocks: SustainabilitySpotBlock[];
  eyebrowText: Field<string>;
  title: Field<string>;
  description: Field<string>;
  buttonText: Field<string>;
  infographic: FileField;
  image: ImageField;
};

export type SustainabilitySpotBlock = {
  id: string;
  title: Field<string>;
  description: Field<string>;
};

type SustainabilitySpotProps = {
  fields: SustainabilitySpotFields;
} & ComponentProps;

export const SustainabilitySpot = ({ fields }: SustainabilitySpotProps) => {
  const { infographic, image, blocks, eyebrowText, title, description, buttonText } = fields;
  const infographicTitle = infographic?.value?.title ?? '';
  const infographicSrc = infographic?.value?.src ?? '';

  return (
    <div className="container">
      <div className="mb-12 flex flex-col items-center md:flex-row md:gap-24">
        <div className="w-full md:basis-1/2">
          <Eyebrow text={eyebrowText?.value} className="mb-6" />
          <h2
            className="heading-2"
            dangerouslySetInnerHTML={{ __html: sanitize(title?.value as string) }}
          />
          <div
            className="paragraph-large"
            dangerouslySetInnerHTML={{ __html: sanitize(description?.value as string) }}
          />

          {infographicSrc && (
            <div className="mt-8 hidden items-start justify-start md:flex">
              <Button
                asLink
                download
                aria-label={infographicTitle}
                href={infographicSrc}
                variant={'primary'}
                size="l"
                iconPlacement="right"
                icon={<JabraIcon Icon={icon_download} />}
              >
                {buttonText?.value}
              </Button>
            </div>
          )}
        </div>
        <div className="flex w-full flex-shrink-0 flex-grow-0 flex-col items-center justify-center gap-6 md:basis-1/2">
          <Image field={image} loading="lazy" />
          {infographicSrc && (
            <Button
              className="md:hidden"
              asLink
              download
              aria-label={infographicTitle}
              href={infographicSrc}
              variant={'primary'}
              size="l"
              iconPlacement="right"
              icon={<JabraIcon Icon={icon_download} />}
            >
              {buttonText?.value}
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {blocks?.map((block) => (
          <SustainabilitySpotBlock
            key={block.id}
            description={block.description?.value}
            text={block.title?.value}
          />
        ))}
      </div>
    </div>
  );
};

export default withDatasourceCheck()<SustainabilitySpotProps>(SustainabilitySpot);
